import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { MaskedLines } from "@/components/motion";
import CTAButton from "@/components/CTAButton";
import CommandCenterHero from "@/components/sections/CommandCenterHero";
import { track } from "@/lib/analytics";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 sm:pt-40" data-testid="hero-section">
      {/* backdrop */}
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_50%_0%,black,transparent_70%)]" />
      <div
        className="pointer-events-none absolute left-1/2 top-[-10%] h-[520px] w-[820px] -translate-x-1/2 rounded-full opacity-[0.16] blur-[120px]"
        style={{ background: "var(--brand-gradient)" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-5xl px-5 text-center sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 glass px-4 py-1.5 text-xs font-medium text-muted-foreground"
        >
          <Sparkles className="h-3.5 w-3.5 text-brand-teal" />
          One Business. One Workspace. Connected Intelligence.
        </motion.div>

        <h1 className="font-display text-[2.6rem] font-semibold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLines
            lines={["Run and think about", "your business from"]}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block brand-gradient-text"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.39 }}
            >
              one workspace.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
          className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          Command Center to <span className="text-foreground">run</span> the day, a Board to help you <span className="text-foreground">think</span> about strategy, and every module in between — connected by intelligence that understands your business.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.68 }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <CTAButton to="/signup" variant="gradient" size="lg" data-testid="hero-start-free-btn" onClick={() => track("cta_click", { cta: "start_free", location: "hero" })} className="w-full sm:w-auto">
            Start Free
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </CTAButton>
          <CTAButton to="/product" variant="outline" size="lg" data-testid="hero-explore-product-btn" onClick={() => track("cta_click", { cta: "explore_product", location: "hero" })} className="w-full sm:w-auto">
            Explore Product
          </CTAButton>
        </motion.div>
      </div>

      {/* Product visual — real Command Center in device frames with subtle motion */}
      <div className="relative mx-auto mt-16 max-w-6xl px-5 sm:mt-20 sm:px-8">
        <motion.div style={{ opacity: fade }} className="relative">
          <CommandCenterHero />
        </motion.div>
      </div>
    </section>
  );
}
